import { Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { usePinQuery, useUpdateLoginPin } from "@/api/auth-query";
import { useBanks, useResolveBankDetails, useUpdateBankDetails } from "@/api/bank-query";
import { useUpdateUserProfile } from "@/api/users-query";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form } from "../ui/form";
import { AccountNumberInput } from "./account-number-input";
import { BankSearch } from "./bank-search";
import { PinVerification } from "./pin-verification";

const bankSchema = z.object({
	bankId: z.string().nonempty("Bank name is required"),
	accountNumber: z.string().nonempty("Account number is required").min(10, "Account number must be at least 10 digits")
});

export function AddBankDialog() {
	const [open, setOpen] = useState(false);
	const [step, setStep] = useState<"bank" | "verify-otp">("bank");
	const bankForm = useForm<z.infer<typeof bankSchema>>({
		resolver: zodResolver(bankSchema),
		defaultValues: {
			bankId: "",
			accountNumber: ""
		}
	});

	const bankQuery = useBanks();
	const hasPinQuery = usePinQuery();
	const updatePin = useUpdateLoginPin();
	const updateBank = useUpdateBankDetails();
	const updateProfile = useUpdateUserProfile();
	const resolveDetails = useResolveBankDetails();

	const handleBankSubmit = () => setStep("verify-otp");

	const handlePinSubmit = async (pin: string) => {
		if (hasPinQuery.data?.data) {
			await updateBank.mutateAsync({
				bankAccounts: [
					{
						bankCode: bankForm.getValues().bankId,
						...bankForm.getValues(),
						accountName: resolveDetails.data?.accountName
					}
				],
				pin
			});
		}
		else {
			await updatePin.mutateAsync(pin);
		}
	};

	const handleClose = () => {
		setOpen(false);
		setStep("bank");
		resolveDetails.reset();
		bankForm.reset();
	};

	const handleOpenChange = (newOpenState: boolean) => {
		if (!newOpenState)
			handleClose();
		else
			setOpen(true);
	};

	useEffect(() => {
		const subscription = bankForm.watch((value) => {
			if (value.accountNumber) {
				const handler = setTimeout(() => {
					const values = bankForm.getValues();
					resolveDetails.mutateAsync(values);
				}, 300);

				return () => clearTimeout(handler);
			}
		});
		return () => subscription.unsubscribe();
	}, [bankForm, resolveDetails]);

	useEffect(() => {
		if (updateBank.isSuccess)
			handleClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateBank.isSuccess]);

	return (
		<Dialog
			open={open}
			onOpenChange={handleOpenChange}
		>
			<DialogTrigger asChild>
				<Button className="gap-1 h-12">
					<Plus className="size-4 bg-white text-primary rounded-sm" />
					<span>Add bank</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Bank Setup</DialogTitle>
					<DialogDescription>
						{

							step === "bank"
								? "Set up a bank for your wallet fundings and withdrawals"
								: !hasPinQuery.data?.data
										? (
												<span className="block text-center p-4 my-2 text-sm text-primary bg-hover-secondary rounded-lg" role="alert">
													You currently have no pin. Please set a pin to continue
												</span>
											)
										: "Enter your pin to add this bank account"
						}
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					{step === "bank" && (
						<Form {...bankForm}>
							<form className="space-y-4">
								<BankSearch control={bankForm.control} banks={bankQuery.data || []} isLoading={bankQuery.isLoading} name="bankId" />
								<AccountNumberInput
									name="accountNumber"
									data={resolveDetails.data}
									control={bankForm.control}
									isError={resolveDetails.isError}
									isPending={resolveDetails.isPending}
									bankId={bankForm.watch("bankId")}
								/>
								<Button
									disabled={
										!bankForm.formState.isValid
										|| resolveDetails.isError
										|| resolveDetails.isPending
										|| updateProfile.isPending
									}
									className="w-full h-14"
									onClick={handleBankSubmit}
								>
									{updateProfile.isPending ? <Loader2 size={20} className="animate-spin" /> : "Add bank account"}
								</Button>
							</form>
						</Form>
					)}
					{step === "verify-otp" && (
						<PinVerification
							onSubmit={handlePinSubmit}
							isPending={updatePin.isPending || updateBank.isPending}
							buttonLabel={hasPinQuery.data?.data ? "Submit" : "Create Pin"}
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
